import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Building2, Eye, EyeOff, KeyRound, Loader2, Pencil, Plus, Zap } from "lucide-react";
import { toast } from "sonner";

export default function AdminTenants() {
  const [tenants, setTenants] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "", notification_email: "", notify_tenant_admin: true });
  const [saving, setSaving] = useState(false);
  const [emailEdit, setEmailEdit] = useState(null);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [senhaAtual, setSenhaAtual] = useState(null);
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [revelando, setRevelando] = useState(false);
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmaSenha, setConfirmaSenha] = useState("");
  const [savingSenha, setSavingSenha] = useState(false);
  const [resetando, setResetando] = useState(false);
  const [nameEdit, setNameEdit] = useState(null);
  const [newTenantName, setNewTenantName] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/admin/tenants").then(({ data }) => setTenants(data)).catch(() => setTenants([]));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (emailEdit) {
      setSenhaAtual(null);
      setShowSenhaAtual(false);
      setNovaSenha("");
      setConfirmaSenha("");
    }
  }, [emailEdit]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/tenants", form);
      setDialog({ created: data });
      setForm({ name: "", admin_name: "", admin_email: "", admin_password: "", notification_email: "", notify_tenant_admin: true });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (!tenants) return <Spinner />;

  const submitAdminEmail = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/tenants/${emailEdit.tenantId}/admin-email`, { new_email: newAdminEmail });
      toast.success("E-mail do gestor alterado com sucesso");
      setEmailEdit(null);
      setNewAdminEmail("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const revelarSenha = async () => {
    if (senhaAtual !== null) {
      setShowSenhaAtual(!showSenhaAtual);
      return;
    }
    setRevelando(true);
    try {
      const { data } = await api.get(`/admin/tenants/${emailEdit.tenantId}/admin-senha/reveal`);
      if (data.ok) {
        setSenhaAtual(data.senha);
        setShowSenhaAtual(true);
      } else {
        toast.info("Senha atual indisponível: foi definida antes deste recurso. Use o raio para resetar ou defina uma nova senha.");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setRevelando(false);
    }
  };

  const resetarSenha = async () => {
    if (!window.confirm(`Resetar a senha do gestor de ${emailEdit.tenantName} para "123456"? Ele deverá criar uma nova senha no próximo login.`)) return;
    setResetando(true);
    try {
      await api.post(`/admin/tenants/${emailEdit.tenantId}/admin-reset-senha`);
      setSenhaAtual("123456");
      toast.success("Senha resetada para 123456. O gestor deverá alterá-la no próximo login.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setResetando(false);
    }
  };

  const submitAdminSenha = async (e) => {
    e.preventDefault();
    if (novaSenha !== confirmaSenha) {
      toast.error("As senhas não coincidem");
      return;
    }
    setSavingSenha(true);
    try {
      await api.patch(`/admin/tenants/${emailEdit.tenantId}/admin-senha`, { new_password: novaSenha });
      setSenhaAtual(novaSenha);
      setNovaSenha("");
      setConfirmaSenha("");
      toast.success("Senha do gestor alterada com sucesso");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingSenha(false);
    }
  };

  const submitRename = async (e) => {
    e.preventDefault();
    if (!newTenantName.trim()) {
      toast.error("Informe o nome da empresa");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/tenants/${nameEdit.tenantId}`, { name: newTenantName.trim() });
      toast.success("Nome da empresa atualizado");
      setNameEdit(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-tenants-page">
      <PageHeader title="Tenants" subtitle="Empresas assinantes da plataforma">
        <Button data-testid="new-tenant-button" onClick={() => setDialog("form")}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> Novo Tenant
        </Button>
      </PageHeader>

      {tenants.some((t) => t.conflito_brave) && (
        <div data-testid="brave-conflict-banner"
          className="mb-4 flex items-start gap-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
          <div className="text-xs">
            <p className="font-semibold text-amber-300" data-testid="brave-conflict-banner-title">
              Conflito de integração Brave: dois ou mais tenants apontam para o mesmo empreendimento.
            </p>
            <ul className="mt-1 space-y-0.5 text-amber-200/80">
              {tenants.filter((t) => t.conflito_brave).map((t) => (
                <li key={t.id} data-testid={`brave-conflict-item-${t.slug}`}>
                  <span className="font-medium">{t.name}</span>: {t.conflito_brave}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tenants.length === 0 ? (
        <EmptyState icon={Building2} title="Nenhum tenant cadastrado" subtitle="Crie o primeiro tenant para começar a operar a plataforma." />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Empresa</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Slug</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">E-mail do Gestor</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Assinatura</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Usuários</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Chamados (abertos)</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Criado em</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`tenant-row-${t.slug}`} onClick={() => navigate(`/admin/tenants/${t.id}`)}
                  className="border-b border-zinc-800/60 hover:bg-zinc-800/50 cursor-pointer transition-colors duration-200">
                  <td className="px-4 py-3 text-zinc-100 font-medium">
                    <span className="flex items-center gap-1.5">
                      {t.name}
                      {t.conflito_brave && (
                        <span data-testid={`brave-conflict-badge-${t.slug}`} title={t.conflito_brave}>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                        </span>
                      )}
                      <button type="button" data-testid={`edit-tenant-name-${t.slug}`} title="Renomear empresa"
                        onClick={(e) => { e.stopPropagation(); setNameEdit({ tenantId: t.id, name: t.name }); setNewTenantName(t.name); }}
                        className="text-zinc-500 hover:text-indigo-400 transition-colors duration-200">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{t.slug}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className="flex items-center gap-1.5">
                      <span data-testid={`admin-email-${t.slug}`} className="text-zinc-300">{t.admin_email || "—"}</span>
                      <button type="button" data-testid={`edit-admin-email-${t.slug}`} title="Alterar e-mail do gestor"
                        onClick={(e) => { e.stopPropagation(); setEmailEdit({ tenantId: t.id, tenantName: t.name, currentEmail: t.admin_email }); setNewAdminEmail(""); }}
                        className="text-zinc-500 hover:text-indigo-400 transition-colors duration-200">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`border-0 text-[11px] hover:bg-transparent ${t.plan_status === "active" ? "text-emerald-400 bg-emerald-400/10" : "text-rose-400 bg-rose-400/10"}`}>
                      {t.plan_status === "active" ? "Ativa" : "Suspensa"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{t.users_count}</td>
                  <td className="px-4 py-3 text-zinc-300">{t.tickets_count} ({t.open_tickets})</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          {dialog === "form" ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-medium">Novo Tenant</DialogTitle>
                <DialogDescription className="text-sm text-zinc-400">
                  Cadastre a empresa e o gestor responsável. Uma chave de API será gerada automaticamente.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="tenant-create-form">
                <div>
                  <Label className="text-xs text-zinc-400">Nome da empresa</Label>
                  <Input data-testid="tenant-name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Nome do administrador</Label>
                  <Input data-testid="tenant-admin-name-input" required value={form.admin_name}
                    onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">E-mail do administrador</Label>
                  <Input data-testid="tenant-admin-email-input" type="email" required value={form.admin_email}
                    onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Senha inicial do administrador</Label>
                  <Input data-testid="tenant-admin-password-input" type="password" required minLength={6} value={form.admin_password}
                    onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">E-mail de notificação de chamados</Label>
                  <Input data-testid="tenant-notification-email-input" type="email" required value={form.notification_email}
                    onChange={(e) => setForm({ ...form, notification_email: e.target.value })}
                    placeholder="suporte@empresa.com.br"
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
                </div>
                <div className="flex items-center gap-2.5">
                  <Checkbox id="notify-admin" data-testid="tenant-notify-admin-checkbox" checked={form.notify_tenant_admin}
                    onCheckedChange={(v) => setForm({ ...form, notify_tenant_admin: !!v })} />
                  <Label htmlFor="notify-admin" className="text-xs text-zinc-400 cursor-pointer leading-snug">
                    Enviar cópia para o gestor do tenant (tenant_admin)
                  </Label>
                </div>
                <p className="text-xs text-zinc-500">O admin master da plataforma sempre recebe. Novos chamados e mudanças de status disparam e-mails.</p>
                <Button data-testid="tenant-create-submit" type="submit" disabled={saving}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar Tenant"}
                </Button>
              </form>
            </>
          ) : dialog?.created ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-medium flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-indigo-400" /> Tenant criado com sucesso
                </DialogTitle>
                <DialogDescription className="text-sm text-zinc-400">
                  Guarde as credenciais abaixo — a chave de API não será exibida novamente.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4" data-testid="tenant-created-dialog">
                <p className="text-sm text-zinc-400">
                  Acesso do gestor: <span className="text-zinc-100 font-medium">{dialog.created.admin_email}</span>
                </p>
                <div>
                  <p className="text-xs text-zinc-400 mb-1.5">API Key de integração:</p>
                  <code data-testid="created-api-key" className="block bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 font-mono text-xs text-emerald-400 break-all select-all">
                    {dialog.created.api_key}
                  </code>
                </div>
                <Button data-testid="tenant-created-close" onClick={() => { const id = dialog.created.tenant.id; setDialog(null); navigate(`/admin/tenants/${id}`); }}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md">
                  Ver detalhes e manual de integração
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailEdit} onOpenChange={(v) => !v && setEmailEdit(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">E-mail e Senha do Gestor</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              {emailEdit?.tenantName} — gestor atual: <span className="text-zinc-200">{emailEdit?.currentEmail}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdminEmail} className="space-y-4" data-testid="change-admin-email-form">
            <div>
              <Label className="text-xs text-zinc-400">Novo e-mail de login do gestor</Label>
              <Input data-testid="admin-email-input" type="email" required value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)} placeholder="gestor@empresa.com.br"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>
            <Button data-testid="admin-email-submit" type="submit" disabled={saving}
              className="w-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 rounded-md transition-colors duration-200">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar e-mail"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-1">
            <div className="h-px flex-1 bg-zinc-800" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">Senha</span>
            <div className="h-px flex-1 bg-zinc-800" />
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Senha atual</Label>
            <div className="relative mt-1.5">
              <Input data-testid="admin-senha-atual-input" type={showSenhaAtual ? "text" : "password"} readOnly
                value={senhaAtual ?? "••••••••"}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md pr-20" />
              <button type="button" data-testid="admin-senha-reveal-btn" onClick={revelarSenha}
                aria-label={showSenhaAtual ? "Ocultar senha atual" : "Visualizar senha atual"}
                title={showSenhaAtual ? "Ocultar senha atual" : "Visualizar senha atual"}
                className="absolute right-10 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors duration-200">
                {revelando ? <Loader2 className="h-4 w-4 animate-spin" /> : showSenhaAtual ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button type="button" data-testid="admin-senha-reset-btn" onClick={resetarSenha}
                aria-label="Resetar a Senha" title="Resetar a Senha"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 hover:text-amber-300 transition-colors duration-200">
                {resetando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">O raio reseta a senha para 123456 — o gestor será obrigado a criar uma nova senha no próximo login.</p>
          </div>

          <form onSubmit={submitAdminSenha} className="space-y-4" data-testid="change-admin-senha-form">
            <div>
              <Label className="text-xs text-zinc-400">Nova senha</Label>
              <Input data-testid="admin-senha-nova-input" type="password" required minLength={6} value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Confirmar nova senha</Label>
              <Input data-testid="admin-senha-confirma-input" type="password" required minLength={6} value={confirmaSenha}
                onChange={(e) => setConfirmaSenha(e.target.value)}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <Button data-testid="admin-senha-submit" type="submit" disabled={savingSenha}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
              {savingSenha ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar senha"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!nameEdit} onOpenChange={(v) => !v && setNameEdit(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Renomear empresa</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Nome atual: <span className="text-zinc-200">{nameEdit?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRename} className="space-y-4" data-testid="rename-tenant-form">
            <div>
              <Label className="text-xs text-zinc-400">Novo nome da empresa</Label>
              <Input data-testid="tenant-rename-input" required value={newTenantName}
                onChange={(e) => setNewTenantName(e.target.value)}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <Button data-testid="tenant-rename-submit" type="submit" disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar nome"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
